// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import translate from "@insite/client-framework/Translate";
import { FormField, FormState } from "@stanion/XDL/Utilities/form";
import { maxLengthAttr } from '@stanion/XDL/Utilities/validation';

export interface RequestAddressFormState extends FormState {
    fields: {
        accountNumber: FormField<string>;
        customerName: FormField<string>;
        jobName: FormField<string>;
        jobAddress: FormField<string>;
        jobCity: FormField<string>;
        jobState: FormField<string>;
        jobZip: FormField<string>;
        estimatedRequestedJob: FormField<string>;
        jointCheckAgreement: FormField<boolean>;
        checkAppropriateStatement: FormField<string>;
        jobNumber: FormField<string>;
        defaultPoNumber: FormField<string>;
        jobForemen: FormField<string>;
        projectManagerAddress: FormField<string>;
        projectManagerTelephone: FormField<string>;
        projectManagerEmail: FormField<string>;
        projectManager: FormField<string>;
        generalContractorAddress: FormField<string>;
        generalContractorTelephone: FormField<string>;
        generalContractorEmail: FormField<string>;
        generalContractor: FormField<string>;
        ownerAddress: FormField<string>;
        ownerTelephone: FormField<string>;
        ownerEmail: FormField<string>;
        owner: FormField<string>;
        address: FormField<string>;
        telephone: FormField<string>;
        email: FormField<string>;
        bondedJob: FormField<boolean>;
        bondingCompanyName: FormField<string>;
        bondingCompanyPhone: FormField<string>;
        additionalInformation: FormField<string>;
    };
}

export const initialRequestAddressFormState: RequestAddressFormState = {
    isValid: false,
    isSubmitted: false,
    fields: {
        accountNumber: {
            value: "",
            validators: [],
            attrs: {
                id: "requestAddress_accountNumber",
                name: "accountNumber",
                label: translate("Account Number"),
            },
        },
        customerName: {
            value: "",
            validators: [],
            attrs: {
                id: "requestAddress_customerName",
                name: "customerName",
                label: translate("Customer Number"),
            },
        },
        jobName: {
            value: "",
            validators: ["required"],
            attrs: {
                id: "requestAddress_jobName",
                name: "jobName",
                label: translate("Job Name"),
                required: true,
            },
        },
        jobAddress: {
            value: "",
            validators: ["required"],
            attrs: {
                id: "requestAddress_jobAddress",
                name: "jobAddress",
                label: translate("Job Address"),
                required: true,
            },
        },
        jobCity: {
            value: "",
            validators: ["required"],
            attrs: {
                id: "requestAddress_jobCity",
                name: "jobCity",
                label: translate("Job City"),
                required: true,
            },
        },
        jobState: {
            value: "",
            validators: ["required"],
            attrs: {
                id: "requestAddress_jobState",
                name: "jobState",
                label: translate("Job State"),
                required: true,
            },
        },
        jobZip: {
            value: "",
            validators: ["required", "zipCode"],
            attrs: {
                id: "requestAddress_jobZip",
                name: "jobZip",
                label: translate("Job Zip"),
                required: true,
            },
        },
        estimatedRequestedJob: {
            value: "",
            validators: ["decimal"],
            attrs: {
                id: "requestAddress_estimatedRequestedJob",
                name: "estimatedRequestedJob",
                label: translate("Estimated requested Job"),
                maxLength: maxLengthAttr.qty,
            },
        },
        jointCheckAgreement: {
            value: false,
            validators: [],
            attrs: {
                id: "requestAddress_jointCheckAgreement",
                name: "jointCheckAgreement",
            },
        },
        checkAppropriateStatement: {
            value: "",
            validators: [],
            attrs: {
                id: "requestAddress_appropriateStatement",
                name: "appropriateStatement",
                label: translate("Please check the appropriate statement"),
            },
        },
        jobNumber: {
            value: "",
            validators: [],
            attrs: {
                id: "requestAddress_jobNumber",
                name: "jobNumber",
                label: translate("Job Number"),
            },
        },
        defaultPoNumber: {
            value: "",
            validators: [],
            attrs: {
                id: "requestAddress_defaultPoNumber",
                name: "defaultPoNumber",
                label: translate("Default PO Number"),
            },
        },
        jobForemen: {
            value: "",
            validators: [],
            attrs: {
                id: "requestAddress_jobForemen",
                name: "jobForemen",
                label: translate("Job Foremen"),
            },
        },
        projectManagerAddress: {
            value: "",
            validators: [],
            attrs: {
                id: "requestAddress_projectManagerAddress",
                name: "projectManagerAddress",
                label: translate("Project Manager Address"),
            },
        },
        projectManagerTelephone: {
            value: "",
            validators: ["phone"],
            attrs: {
                id: "requestAddress_projectManagerTelephone",
                name: "projectManagerTelephone",
                label: translate("Project Manager Telephone"),
                placeholder: "( ___ ) ___ - ____",
                maxLength: 14,
                required: false,
            },
        },
        projectManagerEmail: {
            value: "",
            validators: ["email"],
            attrs: {
                type: "email",
                id: "requestAddress_projectManagerEmail",
                name: "projectManagerEmail",
                label: translate("Project Manager Email"),
                maxLength: maxLengthAttr.email,
            },
        },
        projectManager: {
            value: "",
            validators: [],
            attrs: {
                id: "requestAddress_projectManager",
                name: "projectManager",
                label: translate("Project Manager"),
            },
        },
        generalContractorAddress: {
            value: "",
            validators: [],
            attrs: {
                id: "requestAddress_generalContractorAddress",
                name: "generalContractorAddress",
                label: translate("General Contractor Address"),
            },
        },
        generalContractorTelephone: {
            value: "",
            validators: ["phone"],
            attrs: {
                id: "requestAddress_generalContractorTelephone",
                name: "generalContractorTelephone",
                label: translate("General Contractor Telephone"),
                placeholder: "( ___ ) ___ - ____",
                maxLength: 14,
            },
        },
        generalContractorEmail: {
            value: "",
            validators: ["email"],
            attrs: {
                type: "email",
                id: "requestAddress_generalContractorEmail",
                name: "generalContractorEmail",
                label: translate("General Contractor Email"),
                maxLength: maxLengthAttr.email,
            },
        },
        generalContractor: {
            value: "",
            validators: [],
            attrs: {
                id: "requestAddress_generalContractor",
                name: "generalContractor",
                label: translate("General Contractor"),
            },
        },
        ownerAddress: {
            value: "",
            validators: [],
            attrs: {
                id: "requestAddress_ownerAddress",
                name: "ownerAddress",
                label: translate("Owner Address"),
            },
        },
        ownerTelephone: {
            value: "",
            validators: ["phone"],
            attrs: {
                id: "requestAddress_ownerTelephone",
                name: "ownerTelephone",
                label: translate("Owner Telephone"),
                placeholder: "( ___ ) ___ - ____",
                maxLength: 14,
            },
        },
        ownerEmail: {
            value: "",
            validators: ["email"],
            attrs: {
                type: "email",
                id: "requestAddress_ownerEmail",
                name: "ownerEmail",
                label: translate("Owner Email"),
                maxLength: maxLengthAttr.email,
            },
        },
        owner: {
            value: "",
            validators: [],
            attrs: {
                id: "requestAddress_owner",
                name: "owner",
                label: translate("Owner"),
            },
        },
        address: {
            value: "",
            validators: [],
            attrs: {
                id: "requestAddress_address",
                name: "address",
                label: translate("Address"),
            },
        },
        telephone: {
            value: "",
            validators: ["phone"],
            attrs: {
                id: "requestAddress_telephone",
                name: "telephone",
                label: translate("Telephone"),
                placeholder: "( ___ ) ___ - ____",
                maxLength: 14,
            },
        },
        email: {
            value: "",
            validators: ["email"],
            attrs: {
                type: "email",
                id: "requestAddress_email",
                name: "email",
                label: translate("Email"),
                maxLength: maxLengthAttr.email,
            },
        },
        bondedJob: {
            value: false,
            validators: [],
            attrs: {
                id: "requestAddress_bondedJob",
                name: "bondedJob",
                label: translate("Bonded Job?"),
            },
        },
        bondingCompanyName: {
            value: "",
            validators: [],
            attrs: {
                id: "requestAddress_bondingCompanyName",
                name: "bondingCompanyName",
                label: translate("Bonding Company Name"),
            },
        },
        bondingCompanyPhone: {
            value: "",
            validators: ["phone"],
            attrs: {
                id: "requestAddress_bondingCompanyPhone",
                name: "bondingCompanyPhone",
                label: translate("Bonding Company Phone"),
                placeholder: "( ___ ) ___ - ____",
                maxLength: 14,
            },
        },
        additionalInformation: {
            value: "",
            validators: [],
            attrs: {
                id: "requestAddress_additionalInformation",
                name: "additionalInformation",
                label: translate("Additional Information"),
            },
        },
        
    },
};
